import { ChangeEvent, FC, useRef, useState } from "react";
import {
  ArrowRightOutlined,
  ApartmentOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, message, Select } from "antd";
import createApi, { saveAuthStorage } from "../../api";
import AbstractChatScene from "./AbstractChatScene";
import "./Index.css";

type AuthMode = "login" | "register";

interface LoginProps {
  onLogin: () => void;
}

interface LoginFormValues {
  account: string;
  password: string;
}

interface RegisterFormValues extends LoginFormValues {
  inviteCode: string;
  email: string;
  phone: string;
  nickname: string;
}

interface InviteTeamInfo {
  team_admin: {
    username: string;
    nickname: string;
    link_code: string;
  };
  department: string[];
}

const normalizeDepartmentOptions = (department: unknown) => {
  const source = Array.isArray(department)
    ? department
    : String(department || "").split(/[,\s，、]+/);
  return Array.from(
    new Set(source.map((item) => String(item || "").trim()).filter(Boolean))
  );
};

const Login: FC<LoginProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteTeamInfo, setInviteTeamInfo] = useState<InviteTeamInfo | null>(
    null
  );
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [isInviteValid, setIsInviteValid] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentSelectKey, setDepartmentSelectKey] = useState(0);
  const [loginForm] = Form.useForm<LoginFormValues>();
  const [registerForm] = Form.useForm<RegisterFormValues>();
  const inviteFetchTimerRef = useRef<number | null>(null);
  const inviteRequestSeqRef = useRef(0);
  const latestInviteCodeRef = useRef("");

  const handleLogin = async (values: LoginFormValues) => {
    const account = values.account.trim();
    const password = values.password.trim();
    setLoading(true);
    try {
      const { data } = await createApi().post("/user/login", {
        username: account,
        password,
      });
      if (data.code !== 0) {
        message.error(data.message || "登录失败");
        return;
      }
      saveAuthStorage(data.data);
      message.success("登录成功");
      onLogin();
    } catch {
      message.error("登录失败，请检查后端服务是否正常");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: RegisterFormValues) => {
    const account = values.account.trim();
    const password = values.password.trim();
    const inviteCode = values.inviteCode.trim();
    const email = values.email.trim();
    const phone = values.phone.trim();
    const nickname = values.nickname.trim();
    if (!selectedDepartment) {
      message.warning("请选择部门");
      return;
    }
    const department = [selectedDepartment];
    setLoading(true);
    try {
      const { data } = await createApi().post("/user/register", {
        username: account,
        password,
        invite_code: inviteCode,
        team_code: inviteCode,
        mail: email,
        phone,
        nickname,
        department,
      });
      if (data.code !== 0) {
        message.error(data.message || "注册失败");
        return;
      }
      message.success("注册成功，请登录");
      setMode("login");
      loginForm.setFieldsValue({ account, password: "" });
      registerForm.resetFields();
      resetInviteInfo();
    } catch {
      message.error("注册失败，请检查后端服务是否正常");
    } finally {
      setLoading(false);
    }
  };

  const clearDepartmentField = () => {
    setSelectedDepartment("");
    setDepartmentSelectKey((prev) => prev + 1);
  };

  const resetInviteInfo = () => {
    setInviteTeamInfo(null);
    setDepartmentOptions([]);
    setIsInviteValid(false);
    setInviteLoading(false);
    clearDepartmentField();
  };

  const fetchInviteInfo = async (inviteCode: string) => {
    if (!inviteCode) {
      resetInviteInfo();
      return;
    }
    const requestSeq = inviteRequestSeqRef.current + 1;
    inviteRequestSeqRef.current = requestSeq;
    setInviteLoading(true);
    try {
      const { data } = await createApi().post("/user/invite/info", {
        invite_code: inviteCode,
      });
      if (
        requestSeq !== inviteRequestSeqRef.current ||
        latestInviteCodeRef.current !== inviteCode
      ) {
        return;
      }
      if (data.code !== 0) {
        setInviteTeamInfo(null);
        setDepartmentOptions([]);
        setIsInviteValid(false);
        clearDepartmentField();
        return;
      }
      const nextInfo = {
        ...data.data,
        department: normalizeDepartmentOptions(data.data?.department),
      };
      const allowedDepartment = nextInfo.department;
      const currentDepartment = selectedDepartment;
      setInviteTeamInfo(nextInfo);
      setDepartmentOptions(allowedDepartment);
      setIsInviteValid(allowedDepartment.length > 0);
      const nextDepartment = allowedDepartment.includes(currentDepartment)
        ? currentDepartment
        : "";
      setSelectedDepartment(nextDepartment);
      if (!allowedDepartment.length) {
        message.warning("该团队暂未配置部门，请联系团队管理员");
      }
    } catch {
      setInviteTeamInfo(null);
      setDepartmentOptions([]);
      setIsInviteValid(false);
      clearDepartmentField();
    } finally {
      if (requestSeq === inviteRequestSeqRef.current) {
        setInviteLoading(false);
      }
    }
  };

  const handleInviteCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inviteCode = event.target.value.trim();
    latestInviteCodeRef.current = inviteCode;
    if (inviteFetchTimerRef.current) {
      window.clearTimeout(inviteFetchTimerRef.current);
    }
    setInviteTeamInfo(null);
    setDepartmentOptions([]);
    setIsInviteValid(false);
    clearDepartmentField();
    if (!inviteCode) {
      setInviteLoading(false);
      return;
    }
    setInviteLoading(true);
    inviteFetchTimerRef.current = window.setTimeout(() => {
      fetchInviteInfo(inviteCode);
    }, 450);
  };

  return (
    <div className="authPage">
      <AbstractChatScene />

      <section className="authPanel">
        <div className="authPanel__header">
          <div>
            <span className="authPanel__eyebrow">欢迎进入工作台</span>
            <h2>{mode === "login" ? "登录账号" : "注册账号"}</h2>
          </div>
          <div className="authSwitch">
            <button
              className={mode === "login" ? "active" : ""}
              onClick={() => setMode("login")}
              type="button"
            >
              登录
            </button>
            <button
              className={mode === "register" ? "active" : ""}
              onClick={() => setMode("register")}
              type="button"
            >
              注册
            </button>
          </div>
        </div>

        {mode === "login" ? (
          <Form<LoginFormValues>
            form={loginForm}
            layout="vertical"
            className="authForm"
            onFinish={handleLogin}
            autoComplete="off"
          >
            <Form.Item
              label="账号"
              name="account"
              rules={[{ required: true, message: "请输入账号" }]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="请输入团队账号"
              />
            </Form.Item>

            <Form.Item
              label="密码"
              name="password"
              rules={[{ required: true, message: "请输入密码" }]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="请输入登录密码"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="authSubmit"
              loading={loading}
            >
              登录并进入工作台
              <ArrowRightOutlined />
            </Button>
          </Form>
        ) : (
          <Form<RegisterFormValues>
            form={registerForm}
            layout="vertical"
            className="authForm"
            onFinish={handleRegister}
            autoComplete="off"
          >
            <Form.Item
              label="账号"
              name="account"
              rules={[{ required: true, message: "请输入账号" }]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="设置团队账号"
              />
            </Form.Item>

            <Form.Item
              label="密码"
              name="password"
              rules={[
                { required: true, message: "请输入密码" },
                { min: 6, message: "密码至少 6 位" },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="设置登录密码"
              />
            </Form.Item>

            <Form.Item
              label="邀请码"
              name="inviteCode"
              rules={[{ required: true, message: "请输入邀请码" }]}
            >
              <Input
                size="large"
                prefix={<SafetyCertificateOutlined />}
                onChange={handleInviteCodeChange}
                placeholder="请输入团队邀请码"
              />
            </Form.Item>
            {inviteTeamInfo && (
              <div className="authTeamHint">
                <TeamOutlined />
                <div>
                  <strong>{inviteTeamInfo.team_admin.nickname}</strong>
                  <span>
                    团队管理员：{inviteTeamInfo.team_admin.username}
                  </span>
                </div>
              </div>
            )}

            <Form.Item
              label="邮箱账号"
              name="email"
              rules={[
                { required: true, message: "请输入邮箱账号" },
                { type: "email", message: "请输入正确的邮箱格式" },
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="请输入常用邮箱"
              />
            </Form.Item>

            <Form.Item
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: "请输入手机号" },
                { pattern: /^1\d{10}$/, message: "请输入正确的手机号" },
              ]}
            >
              <Input
                size="large"
                prefix={<PhoneOutlined />}
                placeholder="请输入企业钉钉绑定手机号"
              />
            </Form.Item>

            <Form.Item
              label="昵称"
              name="nickname"
              rules={[{ required: true, message: "请输入昵称" }]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="请输入你的昵称"
              />
            </Form.Item>

            <Form.Item label="部门" required>
              <Select
                key={departmentSelectKey}
                size="large"
                disabled={!isInviteValid}
                loading={inviteLoading}
                value={selectedDepartment}
                onChange={(value) => {
                  setSelectedDepartment(value);
                }}
                options={departmentOptions.map((item) => ({
                  label: item,
                  value: item,
                }))}
                notFoundContent={
                  inviteLoading
                    ? "正在校验邀请码..."
                    : inviteTeamInfo
                    ? "该团队暂无可选部门"
                    : "请先输入有效邀请码"
                }
                placeholder={
                  inviteLoading
                    ? "正在校验邀请码..."
                    : isInviteValid
                    ? "请选择加入的部门"
                    : inviteTeamInfo
                    ? "该团队暂无可选部门"
                    : "请先输入有效邀请码"
                }
                suffixIcon={<ApartmentOutlined />}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="authSubmit"
              loading={loading}
            >
              完成注册
              <ArrowRightOutlined />
            </Button>
          </Form>
        )}
      </section>
    </div>
  );
};

export default Login;
